import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import type { FormEvent } from "react";

import { listCompaniesToReview, mergeCompany, reviewCompany } from "../../api/admin";
import type { FlaggedCompany } from "../../api/admin";
import { errorText } from "../../api/client";
import { Button } from "../../components/Button";
import { EmptyState } from "../../components/EmptyState";
import { Field, FormError } from "../../components/Field";
import { Skeleton } from "../../components/Skeleton";
import { useToast } from "../../components/Toast";
import { nl } from "../../i18n/nl";
import { MunicipalityPicker } from "../profile/PreferenceFields";
import { MergeDialog } from "./MergeDialog";
import styles from "./moderation.module.css";
import { REVIEW_KEY, useRefreshModeration } from "./queries";

const t = nl.admin.moderation;

function ReviewItem({ company }: { company: FlaggedCompany }) {
  const toast = useToast();
  const refresh = useRefreshModeration();
  const [name, setName] = useState(company.name);
  const [municipality, setMunicipality] = useState<string | null>(null);
  const [kbo, setKbo] = useState(company.kbo_number ?? "");
  const [merging, setMerging] = useState(false);

  const putBack = useMutation({
    mutationFn: () => reviewCompany(company.id, { approve: false }),
    onSettled: refresh,
  });
  const approve = useMutation({
    mutationFn: () =>
      reviewCompany(company.id, {
        ...(name.trim() !== company.name ? { name: name.trim() } : {}),
        ...(municipality ? { municipality } : {}),
        ...(kbo.trim() && kbo.trim() !== company.kbo_number ? { kbo_number: kbo.trim() } : {}),
        approve: true,
      }),
    onSuccess: (done) => {
      refresh();
      // Undo puts it back on the list; corrected fields stay corrected.
      toast({
        message: t.toasts.approved(done.name),
        onUndo: () => {
          putBack.mutate();
        },
      });
    },
  });
  const merge = useMutation({
    mutationFn: (intoId: string) => mergeCompany(company.id, intoId),
    onSuccess: (into) => {
      setMerging(false);
      refresh();
      toast({ message: t.toasts.samengevoegd(into.name) });
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    approve.mutate();
  }

  return (
    <li className={styles.reviewItem}>
      <form className={styles.reviewForm} noValidate onSubmit={submit} aria-label={company.name}>
        <div className={styles.targetHead}>
          <span className={styles.targetTitle}>{company.name}</span>
          <span className={styles.meta}>
            {company.municipality} · {t.kbo(company.kbo_number ?? null)}
          </span>
        </div>
        <div className={styles.reviewFields}>
          <Field
            label={t.reviewName}
            maxLength={200}
            value={name}
            onChange={(event) => {
              setName(event.target.value);
            }}
          />
          <MunicipalityPicker
            label={t.reviewMunicipality}
            help={t.reviewMunicipalityHelp(company.municipality)}
            value={municipality}
            onChange={setMunicipality}
          />
          <Field
            label={t.reviewKbo}
            inputMode="numeric"
            placeholder="0712.345.630"
            maxLength={20}
            value={kbo}
            onChange={(event) => {
              setKbo(event.target.value);
            }}
          />
        </div>
        <FormError message={approve.error ? errorText(approve.error) : null} />
        <div className={styles.actions}>
          <Button type="submit" variant="primary" disabled={approve.isPending || name.trim().length < 2}>
            {t.approve}
          </Button>
          <Button
            onClick={() => {
              setMerging(true);
            }}
          >
            {t.merge}
          </Button>
        </div>
      </form>
      <MergeDialog
        source={company}
        open={merging}
        onOpenChange={setMerging}
        pending={merge.isPending}
        error={merge.error}
        onConfirm={(into) => {
          merge.mutate(into.id);
        }}
      />
    </li>
  );
}

export function ReviewList() {
  const companies = useQuery({ queryKey: REVIEW_KEY, queryFn: listCompaniesToReview });
  if (companies.isPending) {
    return (
      <div className={styles.skeletons} aria-hidden="true">
        <Skeleton height={160} />
        <Skeleton height={160} />
      </div>
    );
  }
  if (companies.isError) {
    return (
      <EmptyState
        title={t.loadError}
        action={<Button onClick={() => void companies.refetch()}>{nl.common.retry}</Button>}
      />
    );
  }
  if (companies.data.length === 0) return <EmptyState title={t.empty.review} />;
  return (
    <>
      <p className={styles.meta}>{t.reviewLead}</p>
      <ul className={styles.reviewList} aria-label={t.reviewListLabel}>
        {companies.data.map((company) => (
          <ReviewItem key={company.id} company={company} />
        ))}
      </ul>
    </>
  );
}
